import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

import { BanLookupUpdateResponse } from 'src/app/models/ban-lookup.model';
import { Service } from 'src/app/models/service.model';
import * as fromStore from 'src/app/store/';
import { BanLookupService } from '../../services/ban-lookup.service';

interface BanLookupDialog {
  locationAddress: string;
  banLookupId: string;
  customerLocationAlias?: string;
  services?: Service[];
}

@Component({
  selector: 'app-location-name',
  templateUrl: './location-name.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LocationNameComponent implements OnInit {
  readonly hasError = signal(false);

  readonly isSaving = signal(false);

  private readonly fb = inject(FormBuilder);

  readonly locationNameFormGroup = this.fb.nonNullable.group({
    banLookupId: ['', Validators.required],
    locationNameAlias: ['', [Validators.required, Validators.pattern(/\S/)]],
  });

  private readonly store = inject<Store<fromStore.State>>(Store);

  private readonly destroyRef = inject(DestroyRef);

  private readonly dialogRef = inject(MatDialogRef<LocationNameComponent>);

  private readonly banLookupService = inject(BanLookupService);

  readonly dialogData = inject<BanLookupDialog>(MAT_DIALOG_DATA);

  get locationNameAlias(): FormControl<string> {
    return this.locationNameFormGroup.controls.locationNameAlias;
  }

  get hasExistingLocationName(): boolean {
    return !!this.dialogData.customerLocationAlias?.trim();
  }

  get dialogTitle(): string {
    return this.hasExistingLocationName
      ? 'Edit Location Name'
      : 'Add Location Name';
  }

  get saveButtonLabel(): string {
    return this.hasExistingLocationName ? 'Save Changes' : 'Add Name';
  }

  get showLocationNameError(): boolean {
    return (
      this.locationNameAlias.invalid &&
      (this.locationNameAlias.dirty || this.locationNameAlias.touched)
    );
  }

  ngOnInit(): void {
    this.locationNameFormGroup.setValue({
      banLookupId: this.dialogData.banLookupId,
      locationNameAlias: this.dialogData.customerLocationAlias ?? '',
    });
  }

  deleteLocationAlias(): void {
    if (!this.hasExistingLocationName || this.isSaving()) {
      return;
    }

    const { banLookupId } = this.locationNameFormGroup.getRawValue();
    this.submitAliasChange(
      banLookupId,
      '',
      this.banLookupService.deleteLocationAlias(banLookupId),
    );
  }

  saveLocationName(): void {
    if (this.isSaving()) {
      return;
    }

    if (this.locationNameFormGroup.invalid) {
      this.locationNameFormGroup.markAllAsTouched();
      return;
    }

    const { banLookupId, locationNameAlias } =
      this.locationNameFormGroup.getRawValue();
    const alias = locationNameAlias.trim();

    this.submitAliasChange(
      banLookupId,
      alias,
      this.banLookupService.updateBanLookup({
        Id: banLookupId,
        Customer_Location_Alias__c: alias,
      }),
    );
  }

  private submitAliasChange(
    banLookupId: string,
    locationAlias: string,
    request$: Observable<BanLookupUpdateResponse[]>,
  ): void {
    this.hasError.set(false);
    this.isSaving.set(true);

    const updatedServices = this.getUpdatedServices(banLookupId, locationAlias);

    request$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        if (!result[0]?.success) {
          this.hasError.set(true);
          this.isSaving.set(false);
          return;
        }

        this.dispatchUpdatedServices(updatedServices);
        this.dialogRef.close();
      },
      error: () => {
        this.hasError.set(true);
        this.isSaving.set(false);
      },
    });
  }

  private getUpdatedServices(
    banLookupId: string,
    locationAlias: string,
  ): Service[] {
    return (this.dialogData.services ?? []).slice().map((s) => {
      const newServices = { ...s };
      if (newServices.BanLookupId === banLookupId) {
        newServices.CustomerLocationAlias = locationAlias;
      }
      return newServices;
    });
  }

  private dispatchUpdatedServices(services: Service[]): void {
    if (services.length === 0) {
      return;
    }

    this.store.dispatch(new fromStore.UpdateServicesBanLookup(services));
  }
}
